import * as React from "react";
import axios from "axios";
import { Loader2, TrendingUp, TrendingDown, Minus, DollarSign } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./dialog";
import { Input } from "./input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "./select";
import { Button } from "./button";
import { Label, Text, Caption, MonoText } from "./typography";
import { Separator } from "./separator";
import { API_BASE_URL } from '../../lib/api';

const CURRENCIES = [
  { value: "EUR", label: "EUR - Euro" },
  { value: "USD", label: "USD - US Dollar" },
  { value: "GBP", label: "GBP - British Pound" },
  { value: "SEK", label: "SEK - Swedish Krona" },
  { value: "NOK", label: "NOK - Norwegian Krone" },
  { value: "DKK", label: "DKK - Danish Krone" },
];

/**
 * SellHoldingDialog - Dialog for selling a holding (full or partial)
 *
 * Features:
 * - Pre-populated fields from holding data
 * - User inputs: units to sell, sale price per unit, sale date
 * - "Sell All" button for quick full sale
 * - Real-time calculation preview of realized gain/loss
 * - Validation and error handling
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onOpenChange - Callback when open state changes
 * @param {Function} props.onSuccess - Callback when holding is sold successfully
 * @param {Object} props.holding - The holding data to sell
 */
export function SellHoldingDialog({
  open,
  onOpenChange,
  onSuccess,
  holding,
}) {
  const [formData, setFormData] = React.useState({
    unitsSold: "",
    salePrice: "",
    saleCurrency: "", // Will be initialized from holding
    exchangeRate: "", // Exchange rate to EUR
    saleDate: new Date().toISOString().split("T")[0],
    notes: "",
  });

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [loadingRate, setLoadingRate] = React.useState(false);

  // Extract holding data with fallbacks
  const holdingName = holding?.assetName || holding?.name || "Unknown";
  const holdingTicker = holding?.ticker || "";
  const holdingUnits = parseFloat(holding?.units) || 0;
  const holdingPurchasePrice = parseFloat(holding?.purchasePrice) || 0;
  const holdingCurrency = holding?.currency || holding?.purchaseCurrency || "EUR";
  const holdingDateAcquired = holding?.dateAcquired || "";
  const holdingId = holding?.holdingId || holding?.id;

  // Fetch current exchange rate for a currency
  const fetchExchangeRate = React.useCallback(async (currency) => {
    if (currency === "EUR") {
      return 1; // EUR to EUR is always 1
    }

    setLoadingRate(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/exchange-rates/current/${currency}`,
        { withCredentials: true }
      );
      // The API returns rate as currency -> EUR (e.g., 1 USD = 0.92 EUR)
      return response.data?.rate || null;
    } catch (err) {
      console.error("Failed to fetch exchange rate:", err);
      return null;
    } finally {
      setLoadingRate(false);
    }
  }, []);

  // Reset form when dialog opens or holding changes
  React.useEffect(() => {
    if (open && holding) {
      const initialCurrency = holding?.currency || holding?.purchaseCurrency || "EUR";
      setFormData({
        unitsSold: "",
        salePrice: "",
        saleCurrency: initialCurrency,
        exchangeRate: initialCurrency === "EUR" ? "1" : "",
        saleDate: new Date().toISOString().split("T")[0],
        notes: "",
      });
      setError(null);

      // Fetch exchange rate for initial currency
      if (initialCurrency !== "EUR") {
        fetchExchangeRate(initialCurrency).then((rate) => {
          if (rate) {
            setFormData((prev) => ({ ...prev, exchangeRate: rate.toFixed(6) }));
          }
        });
      }
    }
  }, [open, holding, fetchExchangeRate]);

  // Fetch exchange rate when currency changes
  React.useEffect(() => {
    if (open && formData.saleCurrency) {
      if (formData.saleCurrency === "EUR") {
        setFormData((prev) => ({ ...prev, exchangeRate: "1" }));
      } else {
        fetchExchangeRate(formData.saleCurrency).then((rate) => {
          if (rate) {
            setFormData((prev) => ({ ...prev, exchangeRate: rate.toFixed(6) }));
          }
        });
      }
    }
  }, [open, formData.saleCurrency, fetchExchangeRate]);

  // Calculate preview values
  const preview = React.useMemo(() => {
    const unitsSold = parseFloat(formData.unitsSold) || 0;
    const salePrice = parseFloat(formData.salePrice) || 0;
    const exchangeRate = parseFloat(formData.exchangeRate) || 1;

    if (unitsSold <= 0 || salePrice < 0 || holdingPurchasePrice <= 0) {
      return null;
    }

    const saleProceeds = unitsSold * salePrice;
    const costBasis = unitsSold * holdingPurchasePrice;
    const isFullSale = Math.abs(unitsSold - holdingUnits) < 0.0001;

    // Calculate realized gain - convert to common currency if different
    let realizedGain;
    let saleProceedsInEUR;
    let costBasisInEUR;

    if (formData.saleCurrency === holdingCurrency) {
      // Same currency - simple subtraction
      realizedGain = saleProceeds - costBasis;
    } else {
      // Different currencies - convert both to EUR for comparison
      // Exchange rate is saleCurrency -> EUR
      if (formData.saleCurrency === "EUR") {
        saleProceedsInEUR = saleProceeds;
      } else {
        saleProceedsInEUR = saleProceeds * exchangeRate;
      }

      // For cost basis, if holding is in EUR, use as-is
      // Otherwise, we don't have the purchase exchange rate, so show estimate
      if (holdingCurrency === "EUR") {
        costBasisInEUR = costBasis;
      } else {
        // Holding not in EUR - use same exchange rate as approximation
        // (actual calculation will use historical rates on backend)
        costBasisInEUR = costBasis * exchangeRate;
      }

      realizedGain = saleProceedsInEUR - costBasisInEUR;
    }

    const gainPercent = costBasis > 0 ? (realizedGain / (costBasisInEUR || costBasis)) * 100 : 0;

    return {
      saleProceeds,
      costBasis,
      realizedGain,
      gainPercent,
      isFullSale,
      saleProceedsInEUR,
      costBasisInEUR,
      currencyConverted: formData.saleCurrency !== holdingCurrency,
    };
  }, [formData.unitsSold, formData.salePrice, formData.exchangeRate, formData.saleCurrency, holdingPurchasePrice, holdingUnits, holdingCurrency]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSellAll = () => {
    setFormData((prev) => ({
      ...prev,
      unitsSold: holdingUnits.toString(),
    }));
  };

  const validateForm = () => {
    const unitsSold = parseFloat(formData.unitsSold);
    const salePrice = parseFloat(formData.salePrice);

    if (!formData.unitsSold || isNaN(unitsSold) || unitsSold <= 0) {
      return "Units to sell must be a positive number";
    }

    if (unitsSold > holdingUnits) {
      return `Cannot sell more than ${holdingUnits} units`;
    }

    if (!formData.salePrice || isNaN(salePrice) || salePrice < 0) {
      return "Sale price must be a non-negative number";
    }

    if (!formData.saleDate) {
      return "Sale date is required";
    }

    // Validate sale date is not before acquisition date
    if (holdingDateAcquired && formData.saleDate < holdingDateAcquired) {
      return `Sale date cannot be before acquisition date (${holdingDateAcquired})`;
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    if (!holdingId) {
      setError("No holding ID provided");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        unitsSold: parseFloat(formData.unitsSold),
        salePrice: parseFloat(formData.salePrice),
        saleCurrency: formData.saleCurrency,
        exchangeRate: formData.saleCurrency !== "EUR" ? parseFloat(formData.exchangeRate) : 1,
        saleDate: formData.saleDate,
        notes: formData.notes || "",
      };

      const response = await axios.post(
        `${API_BASE_URL}/holdings/${holdingId}/sell`,
        payload,
        { withCredentials: true }
      );

      if (onSuccess) {
        onSuccess(response.data);
      }
      onOpenChange(false);
    } catch (err) {
      let errorMsg =
        err.response?.data?.details ||
        err.response?.data?.error ||
        err.message;

      if (typeof errorMsg === "object" && errorMsg !== null) {
        errorMsg = errorMsg.message || JSON.stringify(errorMsg);
      }

      console.error("Failed to sell holding:", err);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Format currency value
  const formatCurrency = (value, currency = holdingCurrency) => {
    const symbols = {
      EUR: "\u20AC",
      USD: "$",
      GBP: "\u00A3",
      SEK: "kr",
      NOK: "kr",
      DKK: "kr",
    };
    const symbol = symbols[currency] || currency;
    return `${symbol}${value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // Render gain/loss indicator
  const GainIndicator = ({ value, percent, currency = holdingCurrency }) => {
    const isPositive = value > 0;
    const isNegative = value < 0;
    const Icon = isPositive ? TrendingUp : isNegative ? TrendingDown : Minus;
    const colorClass = isPositive
      ? "text-green-600 dark:text-green-400"
      : isNegative
      ? "text-red-600 dark:text-red-400"
      : "text-muted-foreground";

    return (
      <div className={`flex items-center gap-2 ${colorClass}`}>
        <Icon className="h-5 w-5" />
        <span className="text-lg font-semibold">
          {isPositive ? "+" : ""}
          {formatCurrency(value, currency)}
        </span>
        <span className="text-sm">
          ({isPositive ? "+" : ""}
          {percent.toFixed(2)}%)
        </span>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
        <DialogHeader className="pb-2 flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Sell Holding
          </DialogTitle>
          <DialogDescription>
            Sell some or all of your holding and record the realized gain/loss
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 overflow-y-auto flex-1 pr-1">
          {/* Error Message */}
          {error && (
            <div className="rounded-md bg-destructive/10 border border-destructive p-3">
              <Text variant="error" size="sm">
                {error}
              </Text>
            </div>
          )}

          {/* Holding Info Section */}
          <fieldset className="space-y-2 rounded-lg border border-border/50 p-4 bg-muted/30">
            <legend className="px-2 text-sm font-medium text-muted-foreground">
              Current Holding
            </legend>
            <div className="flex items-start justify-between">
              <div>
                <Text className="font-medium">{holdingName}</Text>
                {holdingTicker && (
                  <MonoText size="sm" className="text-muted-foreground">
                    {holdingTicker}
                  </MonoText>
                )}
              </div>
              <div className="text-right">
                <Text size="sm" className="text-muted-foreground">
                  You own
                </Text>
                <Text className="font-semibold">
                  {holdingUnits.toLocaleString(undefined, { maximumFractionDigits: 4 })} units
                </Text>
              </div>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Avg. cost per unit:</span>
              <MonoText>{formatCurrency(holdingPurchasePrice)}</MonoText>
            </div>
            {holdingDateAcquired && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Date acquired:</span>
                <span>{holdingDateAcquired}</span>
              </div>
            )}
          </fieldset>

          {/* Sale Details Section */}
          <fieldset className="space-y-3 rounded-lg border border-border/50 p-4">
            <legend className="px-2 text-sm font-medium text-muted-foreground">
              Sale Details
            </legend>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-foreground text-sm">Units to Sell *</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleSellAll}
                  className="h-7 text-xs"
                >
                  Sell All
                </Button>
              </div>
              <Input
                name="unitsSold"
                type="number"
                step="0.0001"
                min="0"
                max={holdingUnits}
                value={formData.unitsSold}
                onChange={handleInputChange}
                placeholder={`Max: ${holdingUnits}`}
                required
              />
              {parseFloat(formData.unitsSold) > 0 && (
                <Caption className="text-muted-foreground">
                  {parseFloat(formData.unitsSold) === holdingUnits
                    ? "Selling entire position"
                    : `${(holdingUnits - parseFloat(formData.unitsSold)).toLocaleString(undefined, { maximumFractionDigits: 4 })} units will remain`}
                </Caption>
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="text-foreground text-sm">Sale Price per Unit *</Label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <Input
                    name="salePrice"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.salePrice}
                    onChange={handleInputChange}
                    placeholder={`e.g., ${(holdingPurchasePrice * 1.1).toFixed(2)}`}
                    required
                  />
                </div>
                <div className="w-28">
                  <Select
                    value={formData.saleCurrency}
                    onValueChange={(value) => handleSelectChange("saleCurrency", value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCIES.map((c) => (
                        <SelectItem key={c.value} value={c.value}>
                          {c.value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Exchange Rate Input - only show when not EUR */}
            {formData.saleCurrency !== "EUR" && (
              <div className="space-y-1.5">
                <Label className="text-foreground text-sm">
                  Exchange Rate ({formData.saleCurrency} → EUR) *
                </Label>
                <div className="flex gap-2 items-center">
                  <div className="flex-1">
                    <Input
                      name="exchangeRate"
                      type="number"
                      step="0.000001"
                      min="0"
                      value={formData.exchangeRate}
                      onChange={handleInputChange}
                      placeholder={loadingRate ? "Loading..." : "e.g., 0.92"}
                      disabled={loadingRate}
                      required
                    />
                  </div>
                  <Caption className="text-muted-foreground whitespace-nowrap">
                    1 {formData.saleCurrency} = {formData.exchangeRate || "?"} EUR
                  </Caption>
                </div>
                <Caption className="text-muted-foreground">
                  Current market rate pre-filled. Adjust if your broker used a different rate.
                </Caption>
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-foreground text-sm">Sale Date *</Label>
              <Input
                name="saleDate"
                type="date"
                value={formData.saleDate}
                onChange={handleInputChange}
                min={holdingDateAcquired || undefined}
                max={new Date().toISOString().split("T")[0]}
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-foreground text-sm">Notes (optional)</Label>
              <Input
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="Optional notes about this sale..."
              />
            </div>
          </fieldset>

          {/* Preview Section */}
          {preview && (
            <fieldset className="space-y-3 rounded-lg border-2 border-primary/30 p-4 bg-primary/5">
              <legend className="px-2 text-sm font-semibold text-primary">
                Sale Preview
              </legend>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Sale Proceeds:</span>
                  <div className="text-right">
                    <MonoText className="font-medium">
                      {formatCurrency(preview.saleProceeds, formData.saleCurrency)}
                    </MonoText>
                    {preview.currencyConverted && preview.saleProceedsInEUR && formData.saleCurrency !== "EUR" && (
                      <Caption className="text-muted-foreground block">
                        = {formatCurrency(preview.saleProceedsInEUR, "EUR")}
                      </Caption>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Cost Basis:</span>
                  <div className="text-right">
                    <MonoText className="font-medium">
                      {formatCurrency(preview.costBasis, holdingCurrency)}
                    </MonoText>
                    {preview.currencyConverted && preview.costBasisInEUR && holdingCurrency !== "EUR" && (
                      <Caption className="text-muted-foreground block">
                        ≈ {formatCurrency(preview.costBasisInEUR, "EUR")}
                      </Caption>
                    )}
                  </div>
                </div>

                <Separator className="my-2" />

                {!preview.currencyConverted ? (
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Realized Gain/Loss:</span>
                    <GainIndicator
                      value={preview.realizedGain}
                      percent={preview.gainPercent}
                      currency={holdingCurrency}
                    />
                  </div>
                ) : (
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Estimated Gain/Loss (EUR):</span>
                      <GainIndicator
                        value={preview.realizedGain}
                        percent={preview.gainPercent}
                        currency="EUR"
                      />
                    </div>
                    <Caption className="text-muted-foreground block">
                      Based on exchange rate: 1 {formData.saleCurrency} = {formData.exchangeRate} EUR
                    </Caption>
                  </div>
                )}

                {preview.isFullSale && (
                  <Caption className="text-amber-600 dark:text-amber-400 block mt-2">
                    This will delete the holding after sale
                  </Caption>
                )}
              </div>
            </fieldset>
          )}

          <DialogFooter className="pt-2 flex-shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !preview}
              className="bg-primary"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Selling...
                </>
              ) : (
                "Confirm Sale"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default SellHoldingDialog;
